// frontend/src/pages/CentrifugeLaserV2Page.jsx
import React, { useState, useRef, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import CentrifugeLaserV2 from "../components/CentrifugeLaserV2";

export default function CentrifugeLaserV2Page() {
  const [entropyUI, setEntropyUI] = useState(null);
  const [entropyDyn, setEntropyDyn] = useState(null);
  const [loadingUI, setLoadingUI] = useState(true);

  const [running, setRunning] = useState(true);
  const [chaos, setChaos] = useState(false);
  const [glow, setGlow] = useState(true);

  const [speedPct, setSpeedPct] = useState(50);
  const [ringSpeedPct, setRingSpeedPct] = useState(70); // 🔹 nouveau
  const [turboPct, setTurboPct] = useState(0); // 🔹 slider Turbo

  const [flashRatePct, setFlashRatePct] = useState(30);

  const [bloomIntensity, setBloomIntensity] = useState(1.2);
  const [bloomRadius, setBloomRadius] = useState(0.6);
  const [bloomThreshold, setBloomThreshold] = useState(0.0);

  const canvasRef = useRef(null);
  const orbitRef = useRef(null);

  // Dernier instantané de scène remonté par le composant. Dans un ref et non
  // dans un état : il est réécrit à chaque frame, un état déclencherait un
  // rendu par frame pour une donnée que seul le POST consomme.
  const snapshotRef = useRef(null);

  // --- Entropie UI (backend) ---
  useEffect(() => {
    setLoadingUI(true);
    const controller = new AbortController();

    fetch("/api/geometry/centrifuge_laser_v2/entropy", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        chaos,
        glow,
        spiral_speed: speedPct / 100,
        ring_speed: ringSpeedPct / 100,
        flash_rate: flashRatePct / 100,
        bloom_intensity: bloomIntensity,
        bloom_radius: bloomRadius,
        bloom_threshold: bloomThreshold,
      }),
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        if (typeof data.entropy === "number") setEntropyUI(data.entropy);
      })
      .catch(() => setEntropyUI(null))
      .finally(() => setLoadingUI(false));

    return () => controller.abort();
  }, [chaos, glow, speedPct, ringSpeedPct, flashRatePct, bloomIntensity, bloomRadius, bloomThreshold]);

  // --- Entropie Dynamique : l'état PHYSIQUE de la scène affichée ---
  //
  // ⚠️ Ce POST n'envoyait AUCUNE position. Il transmettait `rotation_speed`,
  // un curseur, et `turbulence: chaos ? Math.random() : 0.2` — un tirage. La
  // route en tirait ensuite une « entropie dynamique » à partir d'un
  // `random.uniform(0.8, 1.6)`. Rien là-dedans ne mesurait la scène.
  //
  // Il transporte désormais l'instantané réel : positions affichées, couleurs
  // canoniques, vitesses dérivées du déplacement entre deux frames servies, et
  // les particules d'explosion dans un champ séparé — leur population varie,
  // donc leur indice ne désigne pas le même objet d'une frame à l'autre.
  useEffect(() => {
    let actif = true;
    const controller = new AbortController();

    const envoyer = () => {
      const snap = snapshotRef.current;
      if (!snap || !snap.positions || snap.positions.length === 0) return;

      fetch("/api/geometry/centrifuge_laser_v2/dyn_entropy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(snap),
        signal: controller.signal,
      })
        .then((r) => r.json())
        .then((data) => {
          if (actif && typeof data.entropy_dyn === "number") {
            setEntropyDyn(data.entropy_dyn);
          }
        })
        .catch(() => {});
    };

    const id = setInterval(envoyer, 1000);
    return () => {
      actif = false;
      clearInterval(id);
      controller.abort();
    };
  }, []);

  const entropyTotal =
    entropyUI !== null && entropyDyn !== null
      ? (entropyUI + entropyDyn).toFixed(4)
      : "--";

  return (
    <div
      className="page-container"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px",
      }}
    >
      <h1>Centrifugeuse Laser V2</h1>

      {/* Jauges */}
      <div style={{ display: "flex", gap: 36, marginBottom: 22 }}>
        <div className="card" style={{ minWidth: 170 }}>
          <h2>Entropie UI</h2>
          <div style={{ fontSize: "1.2rem", color: "#03dac6" }}>
            {loadingUI ? "⏳" : entropyUI !== null ? entropyUI.toFixed(4) : "❌"}
          </div>
        </div>
        <div className="card" style={{ minWidth: 170 }}>
          <h2 style={{ color: "#ff0266" }}>Entropie Dynamique</h2>
          <div style={{ fontSize: "1.2rem", color: "#ff0266" }}>
            {entropyDyn !== null ? entropyDyn.toFixed(4) : "⏳"}
          </div>
        </div>
        <div className="card" style={{ minWidth: 180 }}>
          <h2 style={{ color: "#bb86fc" }}>Score Total</h2>
          <div style={{ fontSize: "1.2rem", color: "#bb86fc" }}>
            {entropyTotal}
          </div>
        </div>
      </div>

      {/* Contrôles + Canvas */}
      <div style={{ display: "flex", alignItems: "center", gap: "28px" }}>
        {/* Contrôles */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            minWidth: 260,
          }}
        >
          <button
            className="generate-button"
            onClick={() => setRunning((p) => !p)}
          >
            {running ? "⏸ Stop Animation" : "▶ Start Animation"}
          </button>

          <button
            onClick={() => setChaos((p) => !p)}
            style={{
              padding: "12px 18px",
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor: chaos ? "#ff0266" : "#03dac6",
              fontWeight: "bold",
            }}
          >
            {chaos ? "Désactiver Chaos" : "Activer Chaos"}
          </button>

          <button
            onClick={() => setGlow((p) => !p)}
            style={{
              padding: "12px 18px",
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor: glow ? "#ffd600" : "#666",
              fontWeight: "bold",
            }}
          >
            {glow ? "Désactiver Glow" : "Activer Glow"}
          </button>

          {/* Sliders */}
          <div className="input-group">
            <label>Vitesse centrifuge : {speedPct}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={speedPct}
              onChange={(e) => setSpeedPct(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Vitesse anneau : {ringSpeedPct}%</label>
            <input
              type="range"
              min={0}
              max={150}
              value={ringSpeedPct}
              onChange={(e) => setRingSpeedPct(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Turbo : {turboPct}%</label>
            <input
              type="range"
              min={0}
              max={200}
              value={turboPct}
              onChange={(e) => setTurboPct(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Flash : {flashRatePct}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={flashRatePct}
              onChange={(e) => setFlashRatePct(Number(e.target.value))}
            />
          </div>

          {/* Sliders Bloom */}
          <div className="input-group">
            <label>Bloom Intensity : {bloomIntensity.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={3}
              step={0.1}
              value={bloomIntensity}
              onChange={(e) => setBloomIntensity(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Bloom Radius : {bloomRadius.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={2}
              step={0.1}
              value={bloomRadius}
              onChange={(e) => setBloomRadius(Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Bloom Threshold : {bloomThreshold.toFixed(2)}</label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={bloomThreshold}
              onChange={(e) => setBloomThreshold(Number(e.target.value))}
            />
          </div>
        </div>

        {/* Canvas */}
        <div
          className="card"
          style={{ width: 600, height: 480, background: "#181818" }}
        >
          {/*
            Caméra reculée de 25 à 32. La scène canonique s'étend jusqu'au
            rayon de l'enceinte du billard — 8 — plus l'hélice de cubes, là où
            la composition locale précédente tenait dans un rayon de ~6. On
            ajuste le point de vue, jamais la scène.
          */}
          <Canvas
            ref={canvasRef}
            camera={{ position: [0, 6, 32], fov: 60 }}
            style={{ width: 600, height: 480, borderRadius: "10px" }}
          >
            <ambientLight intensity={0.7} />
            <pointLight position={[30, 40, 30]} intensity={1.2} />
            <CentrifugeLaserV2
              running={running}
              chaos={chaos}
              glow={glow}
              speedPct={speedPct}
              ringSpeedPct={ringSpeedPct}
              turboMode={turboPct / 100}  // 🔹 valeur normalisée (0 → 2.0)
              flashRatePct={flashRatePct}
              bloomIntensity={bloomIntensity}
              bloomRadius={bloomRadius}
              bloomThreshold={bloomThreshold}
              // Conserve le dernier instantané pour le prochain POST. Le
              // composant ne calcule plus d'entropie : il n'a rien à en dire.
              onSnapshot={(snap) => {
                snapshotRef.current = snap;
              }}
            />
            <OrbitControls
              ref={orbitRef}
              enableZoom={true}
              enablePan={true}
              minDistance={5}
              maxDistance={100}
              target={[0, 0, 0]}
            />
          </Canvas>
        </div>
      </div>
    </div>
  );
}
